import React, { useEffect, useState } from 'react';
import styles from './Statistics.module.css';
import {
  getStats,
  getSalesTrends,
  getUploadsTrends,
  getUserActivityTrends,
  getEventAttendanceStats,
  getPurchasesStats,
} from '../../Services/arndAPI';
import Loader from '../../Components/Loader/Loader';
import PageHeader from '../../Components/PageHeader/PageHeader';
import Page from '../../Components/Page/Page';

// SCRUM-431: revenue is dollars paid by parents (PurchasedAlbum/PurchasedPhoto.pricePaid).
const usd = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Statistics() {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [stats, setStats] = useState(null);
  const [salesTrends, setSalesTrends] = useState([]);
  const [uploadsTrends, setUploadsTrends] = useState([]);
  const [userTrends, setUserTrends] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [purchases, setPurchases] = useState([]);

  useEffect(() => {
    async function fetchAll() {
      setLoading(true);
      setStatus('');
      try {
        const [s, sales, uploads, users, att, pur] = await Promise.all([
          getStats(),
          getSalesTrends(),
          getUploadsTrends(),
          getUserActivityTrends(),
          getEventAttendanceStats(),
          getPurchasesStats(),
        ]);
        setStats(s);
        setSalesTrends(sales);
        setUploadsTrends(uploads);
        setUserTrends(users);
        setAttendance(att);
        setPurchases(pur);
      } catch {
        setStatus('Error loading statistics.');
      } finally {
        setLoading(false);
      }
    }
    fetchAll();
  }, []);

  if (loading) return <Loader />;

  return (
    <Page>
      <PageHeader title="Statistics & Analytics" />
      {status && <div className={styles.status}>{status}</div>}
      {stats && (
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <div className={styles.statLabel}>Total Users</div>
            <div className={styles.statValue}>{stats.users}</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statLabel}>Total Photos</div>
            <div className={styles.statValue}>{stats.photos}</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statLabel}>Total Events</div>
            <div className={styles.statValue}>{stats.events}</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statLabel}>Total Orders</div>
            <div className={styles.statValue}>{stats.orders}</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statLabel}>Total Revenue</div>
            <div className={styles.statValue}>{usd(stats.revenue)}</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statLabel}>RSVPs</div>
            <div className={styles.statValue}>{stats.rsvps}</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statLabel}>Attendees</div>
            <div className={styles.statValue}>{stats.attendees}</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statLabel}>Purchased Photos</div>
            <div className={styles.statValue}>{stats.purchasedPhotos}</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statLabel}>Purchased Albums</div>
            <div className={styles.statValue}>{stats.purchasedAlbums}</div>
          </div>
        </div>
      )}

      <div className={styles.section}>
        <div className={styles.sectionTitle}>Sales Trends (Last 12 Months)</div>
        <table className={styles.trendsTable}>
          <thead>
            <tr>
              <th>Month</th>
              <th>Revenue</th>
              <th>Orders</th>
            </tr>
          </thead>
          <tbody>
            {salesTrends.map((row, i) => (
              <tr key={i}>
                <td>{row.month}</td>
                <td>{usd(row.revenue)}</td>
                <td>{row.orders}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>Photo Uploads Trends (Last 12 Months)</div>
        <table className={styles.trendsTable}>
          <thead>
            <tr>
              <th>Month</th>
              <th>Uploads</th>
            </tr>
          </thead>
          <tbody>
            {uploadsTrends.map((row, i) => (
              <tr key={i}>
                <td>{row.month}</td>
                <td>{row.uploads}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>New Users Trends (Last 12 Months)</div>
        <table className={styles.trendsTable}>
          <thead>
            <tr>
              <th>Month</th>
              <th>New Users</th>
            </tr>
          </thead>
          <tbody>
            {userTrends.map((row, i) => (
              <tr key={i}>
                <td>{row.month}</td>
                <td>{row.new_users}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>Event Attendance</div>
        <table className={styles.attendanceTable}>
          <thead>
            <tr>
              <th>Event</th>
              <th>Date</th>
              <th>RSVPs</th>
              <th>Attendee IDs</th>
            </tr>
          </thead>
          <tbody>
            {attendance.map((row, i) => (
              <tr key={i}>
                <td>{row.name}</td>
                <td>{row.date ? new Date(row.date).toLocaleDateString() : '-'}</td>
                <td>{row.rsvpCount}</td>
                <td>{row.attendeeIds.join(', ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>Purchases Per User</div>
        <table className={styles.purchasesTable}>
          <thead>
            <tr>
              <th>User</th>
              <th>Email</th>
              <th>Photos</th>
              <th>Albums</th>
            </tr>
          </thead>
          <tbody>
            {purchases.map((row, i) => (
              <tr key={i}>
                <td>{row.fullName}</td>
                <td>{row.email}</td>
                <td>{row.photos}</td>
                <td>{row.albums}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Page>
  );
}
